import commonFunc from "@/common.func";

// Yüz tanıma sonuçlarında foto/biyografi satırları artık kimlikno ile değil sonuç id'si
// (milvus vektör id) ile eşleşir; kimlikno istemciye hiç gönderilmez.
//
// Id'ler int64'tür ve 2^53'ü aşar: wire'da STRING gelir. Bu id hem join hem kimlik çözme
// anahtarı olduğundan hassasiyet kaybı yanlış kişinin kimliğinin çözülmesi demektir —
// bu yüzden Number()/parseInt UYGULANMAZ, karşılaştırmalar String() ile yapılır.

// Bir foto satırının hangi sonuç(lar)a ait olduğunu döndürür.
// Tekil `resultId` ve çoğul `resultIds` biçimlerinin ikisini de kabul eder.
export function getPhotoResultIds(photoResult) {
  if (!photoResult) {
    return [];
  }
  const resultIds = photoResult.resultIds || photoResult.result_ids;
  if (Array.isArray(resultIds)) {
    return resultIds.filter((id) => id !== null && id !== undefined).map((id) => String(id));
  }
  const resultId = photoResult.resultId !== undefined ? photoResult.resultId : photoResult.result_id;
  if (resultId === null || resultId === undefined) {
    return [];
  }
  return [String(resultId)];
}

// Bir eşleşme satırının join/çözme anahtarı.
export function getResultKey(item) {
  return item && item.id !== null && item.id !== undefined ? String(item.id) : "";
}

const SOURCE_CODE_FIELDS = ["kaynakkod", "kaynakKod", "kaynak_kod", "sourceCode", "source_code"];

export function getFaceSourceCode(item) {
  if (!item) {
    return null;
  }
  for (let i = 0; i < SOURCE_CODE_FIELDS.length; i += 1) {
    const value = item[SOURCE_CODE_FIELDS[i]];
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      return value;
    }
  }
  return null;
}

export function sourceCodeKey(value) {
  return value === null || value === undefined ? null : String(value).trim().toLocaleLowerCase("tr-TR");
}

// coredb.sahisresim_kaynakkod satirlarini kod -> aciklama haritasina cevirir.
// Aciklamasi bos olan kod haritaya GIRMEZ: boylece formatFaceSourceLabel dogal olarak
// ham koda duser ve "aciklama varsa aciklama, yoksa kod" davranisi tek yerde toplanir.
export function buildFaceSourceLabelMap(items) {
  const labelMap = new Map();
  (items || []).forEach((item) => {
    const key = sourceCodeKey(getFaceSourceCode(item));
    if (key === null) {
      return;
    }
    const description = item.kaynakack || item.kaynakAck || item.kaynak_ack;
    if (typeof description === "string" && description.trim() !== "") {
      labelMap.set(key, description.trim());
    }
  });
  return labelMap;
}

// Ham kaynak kodunun kullaniciya gosterilecek etiketi: aciklama varsa aciklama,
// yoksa kodun kendisi. Kod yoksa (ornegin VIP maskeli eslesme) null — hicbir sey basilmaz.
export function formatFaceSourceCodeLabel(sourceCode, labelMap) {
  if (sourceCode === null || sourceCode === undefined || String(sourceCode).trim() === "") {
    return null;
  }
  const description = labelMap ? labelMap.get(sourceCodeKey(sourceCode)) : null;
  return description || String(sourceCode);
}

// Bir foto/eslesme satirinin kaynak etiketi.
export function formatFaceSourceLabel(item, labelMap) {
  return formatFaceSourceCodeLabel(getFaceSourceCode(item), labelMap);
}

export function toFacePhotoDataUrl(value) {
  if (!value || typeof value !== "string") {
    return null;
  }
  return /^data:image\//.test(value) ? value : `data:image/jpeg;base64,${value}`;
}

function firstFiniteNumber(item, fields) {
  for (let i = 0; i < fields.length; i += 1) {
    const rawValue = item && item[fields[i]];
    if (rawValue === null || rawValue === undefined || rawValue === "") {
      continue;
    }
    const value = Number(rawValue);
    if (Number.isFinite(value)) {
      return value;
    }
  }
  return null;
}

function isExactPhotoEntry(entry, resultKey) {
  const item = entry.raw;
  const exactFields = [
    "id",
    "resultId",
    "result_id",
    "milvusId",
    "milvus_id",
    "vectorId",
    "vector_id",
    "matchedResultId",
    "matched_result_id",
  ];
  return exactFields.some((field) => {
    const value = item && item[field];
    return value !== null && value !== undefined && String(value) === resultKey;
  });
}

function photoEntryPriority(entry, resultKey) {
  const item = entry.raw;
  const markedClosest = !!(
    item &&
    (item.isClosest || item.is_closest || item.isMatched || item.is_matched || item.isNearest || item.is_nearest)
  );
  return {
    exact: isExactPhotoEntry(entry, resultKey) ? 1 : 0,
    markedClosest: markedClosest ? 1 : 0,
    similarity: firstFiniteNumber(item, ["photoSimilarity", "photo_similarity", "similarity", "score"]),
    distance: firstFiniteNumber(item, ["photoDistance", "photo_distance", "distance"]),
    rank: firstFiniteNumber(item, ["photoRank", "photo_rank", "rank"]),
  };
}

function comparePhotoEntries(left, right, resultKey) {
  const a = photoEntryPriority(left, resultKey);
  const b = photoEntryPriority(right, resultKey);
  if (a.exact !== b.exact) return b.exact - a.exact;
  if (a.markedClosest !== b.markedClosest) return b.markedClosest - a.markedClosest;
  if (a.similarity !== null || b.similarity !== null) {
    if (a.similarity === null) return 1;
    if (b.similarity === null) return -1;
    if (a.similarity !== b.similarity) return b.similarity - a.similarity;
  }
  if (a.distance !== null || b.distance !== null) {
    if (a.distance === null) return 1;
    if (b.distance === null) return -1;
    if (a.distance !== b.distance) return a.distance - b.distance;
  }
  if (a.rank !== null || b.rank !== null) {
    if (a.rank === null) return 1;
    if (b.rank === null) return -1;
    if (a.rank !== b.rank) return a.rank - b.rank;
  }
  return left.responseIndex - right.responseIndex;
}

// Foto satirinin kaynak kodunu ve yakinlik bilgisini kaybetmeden sonuc id'sine gore indeksler.
// Bu ayrim, ayni kisi farkli kaynaklarda kayitliysa kartta baska kaynagin fotografini gostermemek icindir.
export function buildPhotoEntriesByResultIdMap(photoResults) {
  const photoMap = new Map();
  const seenByResultId = new Map();
  (photoResults || []).forEach((photoResult, responseIndex) => {
    const imageBase64 = photoResult && (photoResult.imageBase64 || photoResult.image_base64);
    const src = toFacePhotoDataUrl(imageBase64);
    if (!src) {
      return;
    }
    const sourceCode = getFaceSourceCode(photoResult);
    const entry = {
      src,
      sourceCode,
      sourceKey: sourceCodeKey(sourceCode),
      responseIndex,
      raw: photoResult,
    };
    getPhotoResultIds(photoResult).forEach((resultId) => {
      if (!photoMap.has(resultId)) {
        photoMap.set(resultId, []);
        seenByResultId.set(resultId, new Set());
      }
      const dedupeKey = `${entry.sourceKey || ""}\u0000${src}`;
      const seen = seenByResultId.get(resultId);
      if (!seen.has(dedupeKey)) {
        seen.add(dedupeKey);
        photoMap.get(resultId).push(entry);
      }
    });
  });
  return photoMap;
}

// Galeri kisinin tum fotograflarini korur. Karttaki ana fotograf ise once dogrudan eslesen
// vektorden, sonra ayni kaynak kodundaki en yakin kayittan secilir. Diger kaynaklar galeride
// ana fotografin ardindan listelenir. Kaynak bilgisi olmayan eski kayitlar response sirasini korur.
export function getPhotoSelectionForFaceMatch(match, photoEntriesByResultId) {
  if (!match || match.isHidden) {
    return { primary: null, photos: [], sourceCodes: [] };
  }
  const resultKey = getResultKey(match);
  const entries = photoEntriesByResultId ? photoEntriesByResultId.get(resultKey) || [] : [];
  const matchSourceKey = sourceCodeKey(getFaceSourceCode(match));
  const sortedEntries = entries.slice().sort((left, right) => comparePhotoEntries(left, right, resultKey));
  const sameSourceEntries =
    matchSourceKey === null ? [] : sortedEntries.filter((entry) => entry.sourceKey === matchSourceKey);
  const remainingEntries =
    matchSourceKey === null ? sortedEntries : sortedEntries.filter((entry) => entry.sourceKey !== matchSourceKey);
  const hasKnownSource = entries.some((entry) => entry.sourceKey !== null);

  const photos = [];
  // photos ile AYNI sirada kaynak kodlari; galeri altyazisi bu diziden uretilir.
  const sourceCodes = [];
  const seen = new Set();
  const append = (value, sourceCode) => {
    const src = toFacePhotoDataUrl(value);
    if (!src || seen.has(src)) return;
    seen.add(src);
    photos.push(src);
    sourceCodes.push(sourceCode === undefined ? null : sourceCode);
  };
  const directPhotos = [];
  const appendDirect = (value) => {
    const src = toFacePhotoDataUrl(value);
    if (!src || directPhotos.indexOf(src) !== -1) return;
    directPhotos.push(src);
  };
  appendDirect(match.imageBase64);
  appendDirect(match.photoBase64);
  appendDirect(match.image_base64);
  appendDirect(match.photo_base64);
  if (typeof match.photo === "string") {
    appendDirect(match.photo);
  } else if (match.photo && typeof match.photo === "object") {
    const nestedSourceKey = sourceCodeKey(getFaceSourceCode(match.photo));
    if (matchSourceKey === null || nestedSourceKey === null || nestedSourceKey === matchSourceKey) {
      appendDirect(match.photo.imageBase64);
      appendDirect(match.photo.image_base64);
    }
  }
  // DIKKAT: append artik iki parametre aliyor — forEach'e dogrudan verilirse ikinci
  // argument olarak INDEX gecer. Kaynak kodu her cagride acikca yazilir.
  // Dogrudan eslesme satirindan gelen fotolarin kaynagi eslesmenin kendi kaynak kodudur.
  const matchSourceCode = getFaceSourceCode(match);
  directPhotos.forEach((src) => append(src, matchSourceCode));
  sameSourceEntries.forEach((entry) => append(entry.src, entry.sourceCode));
  remainingEntries.forEach((entry) => append(entry.src, entry.sourceCode));

  let primary = directPhotos[0] || null;
  if (!primary && sameSourceEntries.length > 0) {
    primary = sameSourceEntries[0].src;
  }
  if (!primary && (matchSourceKey === null || !hasKnownSource)) {
    primary = photos[0] || null;
  }
  return { primary, photos, sourceCodes };
}

// Sonuç listelerindeki serbest metin araması. Yalnızca kartta GÖRÜNEN kişi alanları
// taranır: soyad maskeli, kimlik numarası ise yanıtta hiç gelmediğinden bu iki alan
// ancak "Kişiyi Göster" ile çözüldükten sonra aramaya takılır.
const SEARCHABLE_PERSON_FIELDS = [
  "ad",
  "soyad",
  "identityNumber",
  "babaad",
  "dogumtarih",
  "yas",
  "nufusIlce",
  "ikametIlce",
];

// Adres kartta koşullu basılır: tekli/çoklu sonuç kartında yalnızca ikamet ilçesi
// boşken, toplu arama kartında ise hiç gösterilmez. Aramaya da aynı koşulla girer;
// aksi halde sokak/mahalle adına takılan sonuç, ekranda o metni içermeden listelenir.
function isAddressSearchable(person, options) {
  if (options && options.hideAddress) {
    return false;
  }
  return !person.ikametIlce;
}

// Video sonuçlarındaki kişi aramasıyla aynı normalizasyon: Türkçe karakterler ASCII
// karşılığına indirilir, böylece "yilmaz" da "Yılmaz" kaydını bulur.
export function normalizeSearchText(value) {
  if (value === null || value === undefined) {
    return "";
  }
  return commonFunc.normalizeTurkish(String(value)).trim();
}

export function buildPersonSearchText(person, options) {
  if (!person) {
    return "";
  }
  const fields = isAddressSearchable(person, options)
    ? SEARCHABLE_PERSON_FIELDS.concat(["adres"])
    : SEARCHABLE_PERSON_FIELDS;
  return normalizeSearchText(
    fields
      .map((field) => person[field])
      .filter((value) => value !== null && value !== undefined && value !== "")
      .join(" ")
  );
}

// Boşlukla ayrılan her parça ayrı ayrı aranır: "ahmet yılmaz" ad ve soyad ayrı alanlarda
// olsa da eşleşir. Boş sorgu tüm satırları geçirir.
export function matchesPersonSearch(person, query, options) {
  const tokens = normalizeSearchText(query)
    .split(/\s+/)
    .filter((token) => token.length > 0);
  if (tokens.length === 0) {
    return true;
  }
  const haystack = buildPersonSearchText(person, options);
  if (!haystack) {
    return false;
  }
  return tokens.every((token) => haystack.indexOf(token) !== -1);
}

export default {
  getPhotoResultIds,
  getResultKey,
  getFaceSourceCode,
  toFacePhotoDataUrl,
  buildPhotoEntriesByResultIdMap,
  getPhotoSelectionForFaceMatch,
  normalizeSearchText,
  buildPersonSearchText,
  matchesPersonSearch,
};
